/**
 * Processor Feedback (QC return / rejection) — QC-CORR-MODAL-001 redesign.
 *
 * Orchestrates the premium blocking CorrectionRequiredModal + the compact
 * CorrectionReminderBar around the UNCHANGED data/actions:
 *   GET  /orders/{id}/qc-feedback     (role-gated by the backend, 403 → nothing)
 *   POST /orders/{id}/qc-acknowledge  (existing audit / timestamp behaviour)
 *   QCLifecycleDrawer                 (GET /qc-history)
 *
 * Auto-open is keyed to the specific return event (event_id / iteration /
 * decided_at), so a new QC iteration re-triggers the mandatory modal even if
 * an earlier one was acknowledged. The responsible processor (backend
 * `is_responsible`: submitter / parent or component assignee) cannot dismiss
 * until acknowledged; oversight viewers may close without acknowledging.
 */

import React, { useEffect, useRef, useState } from 'react';
import QCLifecycleDrawer from './QCLifecycleDrawer';
import CorrectionRequiredModal from './CorrectionRequiredModal';
import CorrectionReminderBar from './CorrectionReminderBar';
import { useQcFeedback } from './useQcFeedback';

const seenKey = (orderId, data) =>
  `qc-corr-seen:${orderId}:${data?.feedback?.event_id || ''}:${data?.qc_iteration || 0}:${data?.feedback?.decided_at || ''}`;

// QC-CORRECTION-UI-001: `qcStateKey` = order-level + TAX/MLS component QC state
// from the parent order; any change re-fetches /qc-feedback (no polling).
const ProcessorFeedbackBanner = ({ orderId, orderNumber, currentUser, onAcknowledged, adminActions, qcStateKey }) => {
  const { data, loading, acking, error, acknowledge } = useQcFeedback(orderId, onAcknowledged, qcStateKey);
  const [open, setOpen] = useState(false);
  const [showLifecycle, setShowLifecycle] = useState(false);
  const autoOpenedFor = useRef(null);

  const active = !!data?.feedback && ['returned', 'rejected'].includes(data?.qc_status);
  const isAcked = !!data?.qc_feedback_acknowledged_at;
  const locked = active && !!data?.is_responsible && !isAcked;

  useEffect(() => {
    if (!active) return;
    const key = seenKey(orderId, data);
    if (autoOpenedFor.current === key) return;
    autoOpenedFor.current = key;
    if (locked || !sessionStorage.getItem(key)) setOpen(true);
  }, [active, locked, orderId, data]);

  const close = () => {
    if (data) sessionStorage.setItem(seenKey(orderId, data), '1');
    setOpen(false);
  };

  if (loading || !active) return null;
  const isReturn = data.feedback.kind === 'returned';

  return (
    <>
      <CorrectionReminderBar data={data} isReturn={isReturn} onViewDetails={() => setOpen(true)} />
      <CorrectionRequiredModal
        open={open}
        onOpenChange={(o) => (o ? setOpen(true) : close())}
        data={data}
        isReturn={isReturn}
        acking={acking}
        error={error}
        locked={locked}
        onAcknowledge={acknowledge}
        onContinue={close}
        onViewHistory={() => setShowLifecycle(true)}
        adminActions={adminActions}
      />
      <QCLifecycleDrawer
        orderId={orderId}
        orderNumber={orderNumber || data?.order_number}
        isOpen={showLifecycle}
        onClose={() => setShowLifecycle(false)}
      />
    </>
  );
};

export default ProcessorFeedbackBanner;
